import type { LocationQuery } from 'vue-router';
import type { UrlSerializableFilterState } from '@/types/nmed/nmed-filter-types';
import type { SearchKeyItem } from '@/types/nmed/shared-filter-types';
import { isRangeFilter } from '@/types/nmed/shared-filter-types';

/**
 * URL parameter keys for filter state (short names to keep URLs manageable)
 */
export const FILTER_URL_PARAMS = {
   bureaus: 'bureaus',
   documentTypes: 'docTypes',
   /** @deprecated Use searchFilters instead */
   keywords: 'kw',
   /** @deprecated Use searchFilters instead */
   values: 'val',
   /** Grouped search filters: srf=County:Bernalillo|Santa%20Fe,FacilityName:Plant%20A */
   searchFilters: 'srf',
   globalSearch: 'gs',
   sortField: 'sf',
   sortOrder: 'so',
   yearType: 'yt',
   year: 'y',
   startYear: 'sy',
   endYear: 'ey',
} as const;

/**
 * Creates a default empty filter state
 */
export function createEmptyFilterState(): UrlSerializableFilterState {
   return {
      bureaus: [],
      documentTypes: [],
      searchFilters: {},
      globalSearchKeyword: '',
      sortField: '',
      sortOrder: '',
      yearType: 'single',
      year: '',
      startYear: '',
      endYear: '',
   };
}

/**
 * Encodes grouped search filters to URL format.
 * Format: "County:Bernalillo|Santa%20Fe,FacilityName:Plant%20A"
 * Range filters are encoded as: "ExpiryDate:[RANGE:2024-01-01~2024-12-31]"
 */
function encodeSearchFilters(filters: Record<string, SearchKeyItem[]>): string {
   const entries = Object.entries(filters)
      .filter(([_, values]) => values.length > 0)
      .map(([field, values]) => {
         const encodedField = encodeURIComponent(field);
         const encodedValues = values.map((v) => {
            if (isRangeFilter(v)) {
               // Encode range filter as special format
               return encodeURIComponent(`[RANGE:${v.start_at}~${v.end_at}]`);
            }
            return encodeURIComponent(v);
         }).join('|');
         return `${encodedField}:${encodedValues}`;
      });
   return entries.join(',');
}

/**
 * Decodes URL format back to grouped search filters.
 * Format: "County:Bernalillo|Santa%20Fe,FacilityName:Plant%20A"
 * Range filters are decoded from: "ExpiryDate:[RANGE:2024-01-01~2024-12-31]"
 */
function decodeSearchFilters(encoded: string): Record<string, SearchKeyItem[]> {
   if (!encoded) {
      return {};
   }

   const filters: Record<string, SearchKeyItem[]> = {};

   // Split by comma, but we need to be careful about values that might contain encoded commas
   const entries = encoded.split(',');
   for (const entry of entries) {
      const colonIndex = entry.indexOf(':');
      if (colonIndex === -1) {
         continue;
      }

      const field = decodeURIComponent(entry.substring(0, colonIndex));
      const valuesStr = entry.substring(colonIndex + 1);
      const values: SearchKeyItem[] = valuesStr.split('|')
         .map((v) => {
            const decoded = decodeURIComponent(v);
            // Check if this is an encoded range filter
            const rangeMatch = decoded.match(/^\[RANGE:([^~]+)~([^\]]+)\]$/);
            if (rangeMatch) {
               return {
                  type: 'range' as const,
                  start_at: rangeMatch[1],
                  end_at: rangeMatch[2],
               };
            }
            return decoded;
         })
         .filter(v => v !== '');

      if (field && values.length > 0) {
         filters[field] = values;
      }
   }

   return filters;
}

/**
 * Migrates old parallel arrays format (keywords/values) to grouped format (searchFilters).
 */
function migrateParallelArraysToGrouped(keywords: string[], values: string[]): Record<string, string[]> {
   const filters: Record<string, string[]> = {};
   for (let i = 0; i < keywords.length; i++) {
      const field = keywords[i];
      const value = values[i];
      if (field && value) {
         if (!filters[field]) {
            filters[field] = [];
         }
         if (!filters[field].includes(value)) {
            filters[field].push(value);
         }
      }
   }
   return filters;
}

/**
 * Encodes filter state to URL query params object.
 * Only includes non-empty values to keep URLs clean.
 */
export function encodeFiltersToQuery(state: UrlSerializableFilterState): Record<string, string> {
   const query: Record<string, string> = {};

   if (state.bureaus && state.bureaus.length > 0) {
      query[FILTER_URL_PARAMS.bureaus] = state.bureaus.map(b => encodeURIComponent(b)).join(',');
   }

   if (state.documentTypes && state.documentTypes.length > 0) {
      query[FILTER_URL_PARAMS.documentTypes] = state.documentTypes.map(d => encodeURIComponent(d)).join(',');
   }

   // Encode grouped search filters
   if (state.searchFilters && Object.keys(state.searchFilters).length > 0) {
      query[FILTER_URL_PARAMS.searchFilters] = encodeSearchFilters(state.searchFilters);
   }

   if (state.globalSearchKeyword) {
      query[FILTER_URL_PARAMS.globalSearch] = state.globalSearchKeyword;
   }

   if (state.sortField) {
      query[FILTER_URL_PARAMS.sortField] = state.sortField;
   }

   if (state.sortOrder) {
      query[FILTER_URL_PARAMS.sortOrder] = state.sortOrder;
   }

   // Year filters - only include yearType if using range mode
   if (state.yearType === 'range') {
      query[FILTER_URL_PARAMS.yearType] = state.yearType;
      if (state.startYear) {
         query[FILTER_URL_PARAMS.startYear] = state.startYear;
      }
      if (state.endYear) {
         query[FILTER_URL_PARAMS.endYear] = state.endYear;
      }
   }
   else if (state.year) {
      query[FILTER_URL_PARAMS.year] = state.year;
   }

   return query;
}

/**
 * Decodes URL query params to filter state.
 * Returns null if no filter params present.
 * Supports both old format (kw/val) and new format (srf) for backward compatibility.
 */
export function decodeFiltersFromQuery(query: LocationQuery): UrlSerializableFilterState | null {
   const p = FILTER_URL_PARAMS;

   // Check if any filter params exist
   const hasFilterParams = Object.values(p).some(key => query[key] !== undefined);
   if (!hasFilterParams) {
      return null;
   }

   const getString = (key: string): string => {
      const value = query[key];
      if (Array.isArray(value)) {
         return value[0] ?? '';
      }
      return value ?? '';
   };

   const getArray = (key: string): string[] => {
      const value = getString(key);
      if (!value) {
         return [];
      }
      return value.split(',').map(v => decodeURIComponent(v));
   };

   // Determine year type from URL params
   const yearType = getString(p.yearType) as 'single' | 'range' || 'single';

   // Handle search filters - prefer new format, fall back to old format
   let searchFilters: Record<string, SearchKeyItem[]> = {};
   const searchFiltersStr = getString(p.searchFilters);
   if (searchFiltersStr) {
      // New grouped format: srf=County:Bernalillo|Santa%20Fe,FacilityName:Plant%20A
      searchFilters = decodeSearchFilters(searchFiltersStr);
   }
   else {
      // Old parallel arrays format: kw=County,County&val=Bernalillo,Santa%20Fe
      const keywords = getArray(p.keywords);
      const values = getArray(p.values);
      if (keywords.length > 0 && values.length > 0) {
         searchFilters = migrateParallelArraysToGrouped(keywords, values);
      }
   }

   return {
      bureaus: getArray(p.bureaus),
      documentTypes: getArray(p.documentTypes),
      searchFilters,
      globalSearchKeyword: getString(p.globalSearch),
      sortField: getString(p.sortField),
      sortOrder: getString(p.sortOrder) as 'asc' | 'desc' | '',
      yearType,
      year: getString(p.year),
      startYear: getString(p.startYear),
      endYear: getString(p.endYear),
   };
}

/**
 * Serializes a SearchKeyItem to a string for comparison
 */
function serializeSearchKeyItem(item: SearchKeyItem): string {
   if (isRangeFilter(item)) {
      return `[RANGE:${item.start_at}~${item.end_at}]`;
   }
   return item;
}

/**
 * Checks if two search filter objects are equal
 */
function searchFiltersEqual(a: Record<string, SearchKeyItem[]>, b: Record<string, SearchKeyItem[]>): boolean {
   const keysA = Object.keys(a).sort();
   const keysB = Object.keys(b).sort();

   if (keysA.length !== keysB.length) {
      return false;
   }

   for (let i = 0; i < keysA.length; i++) {
      if (keysA[i] !== keysB[i]) {
         return false;
      }
      const valuesA = a[keysA[i]].map(serializeSearchKeyItem).sort();
      const valuesB = b[keysB[i]].map(serializeSearchKeyItem).sort();
      if (valuesA.length !== valuesB.length) {
         return false;
      }
      for (let j = 0; j < valuesA.length; j++) {
         if (valuesA[j] !== valuesB[j]) {
            return false;
         }
      }
   }

   return true;
}

/**
 * Checks if two filter states are equal
 */
export function areFiltersEqual(a: UrlSerializableFilterState, b: UrlSerializableFilterState): boolean {
   const arraysEqual = (arr1: string[], arr2: string[]) =>
      arr1.length === arr2.length && arr1.every((v, i) => v === arr2[i]);

   return (
      arraysEqual(a.bureaus || [], b.bureaus || [])
      && arraysEqual(a.documentTypes || [], b.documentTypes || [])
      && a.globalSearchKeyword === b.globalSearchKeyword
      && a.sortField === b.sortField
      && a.sortOrder === b.sortOrder
      && searchFiltersEqual(a.searchFilters || {}, b.searchFilters || {})
      && a.yearType === b.yearType
      && a.year === b.year
      && a.startYear === b.startYear
      && a.endYear === b.endYear
   );
}

/**
 * Checks if filter state has any active filters
 */
export function hasActiveFilters(state: UrlSerializableFilterState): boolean {
   return !!(
      (state.bureaus && state.bureaus.length > 0)
      || (state.documentTypes && state.documentTypes.length > 0)
      || state.globalSearchKeyword
      || (state.searchFilters && Object.keys(state.searchFilters).length > 0)
      || state.year
      || state.startYear
      || state.endYear
   );
}
